import { DEFAULT_TIMEZONE } from '@yoyoclub/shared';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dateLocale } from '../../i18n/locale';
import { localize } from '../../i18n/script';
import type { UserBookingsResponse } from '../../services/bookingApi';

interface TransferConfirmationModalProps {
  booking: UserBookingsResponse;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export default function TransferConfirmationModal({
  booking,
  onClose,
  onConfirm,
}: TransferConfirmationModalProps) {
  const { t, i18n } = useTranslation(['booking']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activityTitle = localize(
    booking.session.activity.titleCN,
    booking.session.activity.titleEN,
    i18n.language
  );

  const sessionDate = new Date(booking.session.startTime).toLocaleDateString(
    dateLocale(i18n.language),
    {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: DEFAULT_TIMEZONE,
    }
  );

  const isPassBooking = booking.totalAmount === 0;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to transfer booking');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('booking:transferModal.title')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-gray-700">
            {t('booking:transferModal.confirmMessage', {
              activityName: activityTitle,
              date: sessionDate,
            })}
          </p>

          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <p className="text-sm text-gray-600">{t('booking:transferModal.spotsReleased')}</p>
            <p className="text-sm text-gray-600">
              {isPassBooking
                ? t('booking:transferModal.refundInfoPass')
                : t('booking:transferModal.refundInfo')}
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('booking:transferModal.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:opacity-50"
          >
            {isSubmitting
              ? t('booking:myBookings.transferring')
              : t('booking:transferModal.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
